import type { Messages } from '../catalogue.en'

/**
 * The Control Center's external-service sections: Telegram, WhatsApp, Google Document
 * AI, Google sign-in, AI configuration and stored secrets. Mirrors
 * `en/controlCenterIntegrations.ts`.
 *
 * Composed into the public catalogue by `catalogue.ru.ts`. Nothing
 * imports this module directly: consumers ask the catalogue, so how the
 * messages are stored stays this layer's business.
 */
export const controlCenterIntegrations: Partial<Messages> = {
  'ai.functions.function': "Функция",
  'ai.functions.pipeline': "Пайплайн",
  'ai.functions.pipelineFor': "Пайплайн: {name}",
  'ai.functions.prompts': "Промпты",
  'ai.functions.parametersFor': "Параметры: {name}",
  'ai.functions.enabled': "Включена",
  'ai.functions.enabledFor': "Включить: {name}",
  'ai.functions.unsaved': "Не сохранено",
  'ai.functions.pendingFor': "Не сохранён выбор: {name}.",
  'ai.functions.apply': "Применить выбор пайплайна",
  'ai.functions.cancel': "Отменить изменения выбора",
  'ai.functions.applied': "Сохранено. Новые запросы используют этот выбор.",
  'ai.functions.noPrompts': "Без промптов из реестра",
  'ai.functions.promptUnavailable': "Сведения недоступны",
  'ai.functions.fixedSettings': "Эти параметры выполнения доступны только для просмотра.",
  'ai.functions.empty': "Нет доступных функций ИИ",
  'ai.functions.governmentId': "Извлечение реквизитов удостоверения",
  'ai.functions.insurance': "Извлечение реквизитов страховки",
  'ai.functions.transcription': "Расшифровка речи в текст",
  'ai.functions.transcriptionStage': "Расшифровка речи",
  'ai.functions.dataOCR': "Google получает изображения страниц. OpenAI получает распознанный текст и нормализованные изображения для проверки.",
  'ai.functions.dataDictation': "OpenAI получает аудио, затем расшифровку и схему клинической формы для заполнения полей.",
  'ai.functions.dataAudio': "OpenAI получает записанное аудио.",
  'ai.functions.dataText': "OpenAI получает выбранный текст и необходимые языковые материалы.",
  'ai.functions.dataDocument': "OpenAI получает содержимое документа, включая изображения страниц, необходимые этой функции.",
  'ai.functions.dataLabGooglePages': "Google Document AI получает каждую страницу отдельно. OpenAI получает распознанный текст всех страниц, затем изображения и текст подтверждённых вами страниц.",
  'ai.functions.dataQuestion': "OpenAI получает клинический PDF, вопрос и предыдущие вопросы этого врача по событию.",
  'ai.functions.dataClinical': "OpenAI получает клинические сведения события для предложения рекомендаций.",
  'ai.functions.dataFilters': "OpenAI получает заголовки столбцов и описание поиска; строки таблицы не передаются.",
  'ai.functions.dataNames': "OpenAI получает исходный текст и запрошенные языки.",
  'ai.functions.dataIssue': "OpenAI получает описание обращения; вложения не передаются.",
  'ai.functions.dataExternalEntries': "OpenAI получает распознанный текст страниц выбранных документов, чтобы разметить их, сгруппировать по случаям оказания помощи и составить запись на каждый случай.",
  'ai.functions.dataSummary': "OpenAI получает заполненные поля записей карты и распознанный текст документов пациента.",
  'ai.functions.dataUnknown': "Данные, необходимые этой функции, передаются указанным провайдерам.",
  'ai.functions.title': "Функции ИИ",
  'ai.functions.description': 'Отключение блокирует новые запросы; принятые могут завершиться.',
  'ai.functions.loading': "Загрузка настроек функций…",
  'ai.functions.unavailable': "Настройки функций недоступны. Повторите после их загрузки.",
  'ai.functions.disabled': "Отключено",
  'ai.functions.disabledFor': "Отключить: {name}",
  'ai.functions.disabledHint': "Отключено в настройках ИИ Центра управления.",
  'ai.functions.external': "Данные передаются внешним провайдерам:",
  'ai.functions.local': "Обработка в локальном контуре.",
  'ai.functions.googleOpenai': "Распознавание Google + проверка OpenAI",
  'ai.functions.googleIDOpenai': "Распознавание Google + извлечение OpenAI",
  'ai.functions.googlePagesOpenai': "Google постранично + извлечение OpenAI",
  'ai.functions.dataIDGoogleLocal': "Google получает страницы документа для распознавания. Локальная модель получает текст OCR и выровненные изображения; они не отправляются в OpenAI.",
  'ai.functions.dataIDGoogleOpenai': "Google получает страницы документа для распознавания. OpenAI получает текст OCR и выровненные изображения для извлечения реквизитов документа.",
  'ai.functions.dataInsuranceGoogleOpenai': "Google получает страницы документа для распознавания. OpenAI получает текст OCR и выровненные изображения для извлечения реквизитов страхового полиса.",
  'ai.functions.googleLocal': "Распознавание Google + локальная модель",
  'ai.functions.localOpenai': "Локальное выравнивание + распознавание OpenAI",
  'ai.functions.ocr': "Распознавание текста документа (OCR)",
  'ai.functions.dictation': "Заполнение клинической записи из диктовки",
  'ai.functions.translateDocument': "Перевод документов",
  'ai.functions.translateRecord': "Перевод медицинской записи",
  'ai.functions.summary': "Сводка по документам",
  'ai.functions.questions': "Вопросы по медицинским документам",
  'ai.functions.review': "Предложение клинических рекомендаций",
  'ai.functions.filters': "Подбор фильтров таблицы",
  'ai.functions.localizedText': "Предложения перевода названий",
  'ai.functions.issueTitle': "Предложения заголовков задач",
  'ai.functions.unknown': "Неизвестная функция или пайплайн — обновите приложение",
  'ai.functions.unsavedIntent': "Настройки функций ИИ",
  'ai.functions.reload': "Загрузить сохранённые настройки вместо черновика",
  'ai.functions.reloaded': "Сохранённые настройки загружены.",
  'ai.functions.reloadFailed': "Не удалось загрузить настройки. Черновик сохранён.",
  'telegramLink.title': 'Telegram',
  'telegramLink.loading': 'Загрузка подключения к Telegram',
  'telegramLink.loadFailed': 'Не удалось загрузить подключение к Telegram.',
  'telegramLink.connected': 'Подключено',
  'telegramLink.notConnected': 'Не подключено',
  'telegramLink.createInvite': 'Создать ссылку-приглашение',
  'telegramLink.creating': 'Создание ссылки',
  'telegramLink.inviteLink': 'Одноразовая ссылка-приглашение в Telegram',
  'telegramLink.copyInvite': 'Скопировать ссылку-приглашение',
  'telegramLink.openTelegram': 'Открыть Telegram',
  'telegramLink.expiresInDay': 'Эта одноразовая ссылка истекает через 24 часа.',
  'telegramLink.copied': 'Ссылка-приглашение скопирована',
  'telegramLink.copyFailed': 'Не удалось скопировать ссылку-приглашение.',
  'telegramLink.botUnavailable': 'Telegram-бот клиники ещё не настроен.',
  'telegramLink.inviteFailed': 'Не удалось создать ссылку-приглашение.',
  'telegramLink.disconnect': 'Отключить',
  'telegramLink.disconnecting': 'Отключение',
  'telegramLink.disconnectFailed': 'Не удалось отключить Telegram.',
  'whatsappLink.title': 'WhatsApp',
  'whatsappLink.loading': 'Загрузка подключения к WhatsApp',
  'whatsappLink.loadFailed': 'Не удалось загрузить подключение к WhatsApp.',
  'whatsappLink.connected': 'Подключено',
  'whatsappLink.notConnected': 'Не подключено',
  'whatsappLink.createInvite': 'Создать ссылку-приглашение',
  'whatsappLink.creating': 'Создание ссылки',
  'whatsappLink.inviteLink': 'Одноразовая ссылка-приглашение в WhatsApp',
  'whatsappLink.copyInvite': 'Скопировать ссылку-приглашение',
  'whatsappLink.openWhatsApp': 'Открыть WhatsApp',
  'whatsappLink.sendToLink':
    'Ссылка открывает чат WhatsApp с номером клиники. Отправка подготовленного сообщения привязывает телефон, с которого оно отправлено.',
  'whatsappLink.expiresInDay': 'Эта одноразовая ссылка истекает через 24 часа.',
  'whatsappLink.copied': 'Ссылка-приглашение скопирована',
  'whatsappLink.copyFailed': 'Не удалось скопировать ссылку-приглашение.',
  'whatsappLink.senderUnavailable': 'Номер-отправитель WhatsApp клиники ещё не настроен.',
  'whatsappLink.inviteFailed': 'Не удалось создать ссылку-приглашение.',
  'whatsappLink.disconnect': 'Отключить',
  'whatsappLink.disconnecting': 'Отключение',
  'whatsappLink.disconnectFailed': 'Не удалось отключить WhatsApp.',

  'storedSecret.updated': 'Обновлено',
  'storedSecret.length': 'Символов: {length}',


  'googleDocAI.title': 'Google Document AI',
  'googleDocAI.loading': 'Загрузка статуса Google Document AI',
  'googleDocAI.unavailable': 'Зашифрованное хранилище секретов клиники не настроено.',
  'googleDocAI.loadFailed': 'Статус Google Document AI не удалось загрузить.',
  'googleDocAI.serviceAccount': 'Сервисный аккаунт',
  'googleDocAI.project': 'Проект',
  'googleDocAI.key': 'Содержимое файла ключа сервисного аккаунта',
  'googleDocAI.storedKey': 'Закрытый ключ',
  'googleDocAI.set': 'Задать ключ сервисного аккаунта',
  'googleDocAI.change': 'Заменить ключ сервисного аккаунта',
  'googleDocAI.openFile': 'Открыть файл ключа',
  'googleDocAI.keyFilePicker': 'Выберите файл ключа сервисного аккаунта',
  'googleDocAI.fromFile': 'Прочитано из {name}',
  'googleDocAI.fileReadFailed': 'Выбранный файл не удалось прочитать.',
  'googleDocAI.location': 'Расположение процессора',
  'googleDocAI.processorId': 'Идентификатор процессора',
  'googleDocAI.unsavedIntent': 'Ключ Google Document AI',
  'googleDocAI.save': 'Сохранить ключ',
  'googleDocAI.saving': 'Проверка и сохранение',
  'googleDocAI.clear': 'Удалить ключ',
  'googleDocAI.clearing': 'Удаление',
  'googleDocAI.saved': 'Ключ Google Document AI сохранён',
  'googleDocAI.cleared': 'Ключ Google Document AI удалён',
  'googleDocAI.rejected': 'Это не содержимое файла ключа сервисного аккаунта Google.',
  'googleDocAI.settingsRejected': 'Расположение или идентификатор процессора недействительны.',
  'googleDocAI.saveFailed': 'Не удалось сохранить ключ Google Document AI.',
  'googleDocAI.clearFailed': 'Не удалось удалить ключ Google Document AI.',

  "googleSignIn.title": "Вход пациента через Google",
  'googleSignIn.loading': 'Загрузка статуса входа через Google',
  'googleSignIn.loadFailed': 'Не удалось загрузить настройку входа через Google.',
  'googleSignIn.clientId': 'Идентификатор клиента OAuth для веб-приложения',
  'googleSignIn.hint':
    'Публичный идентификатор клиента веб-приложения из Google Cloud Console, оканчивающийся на .apps.googleusercontent.com. Пациенты видят кнопку Google на публичной странице онбординга, только пока он задан.',
  'googleSignIn.set': 'Задать идентификатор клиента',
  'googleSignIn.change': 'Заменить идентификатор клиента',
  'googleSignIn.unsavedIntent': 'Идентификатор клиента для входа через Google',
  'googleSignIn.save': 'Сохранить идентификатор клиента',
  'googleSignIn.saving': 'Проверка и сохранение',
  'googleSignIn.clear': 'Удалить идентификатор клиента',
  'googleSignIn.clearing': 'Удаление',
  'googleSignIn.saved': 'Идентификатор клиента для входа через Google сохранён',
  'googleSignIn.cleared': 'Идентификатор клиента для входа через Google удалён',
  'googleSignIn.rejected':
    'Это не идентификатор клиента веб-приложения Google, оканчивающийся на .apps.googleusercontent.com.',
  'googleSignIn.saveFailed': 'Не удалось сохранить идентификатор клиента для входа через Google.',
  'googleSignIn.clearFailed': 'Не удалось удалить идентификатор клиента для входа через Google.',

  'ai.title': 'ИИ',
  'ai.views.label': 'Подразделы ИИ',
  'ai.views.settings': "Функции",
  'ai.views.settingsDescription': "Пайплайны, промпты и параметры выполнения",
  'ai.views.prompts': 'Промпты',
  'ai.views.promptsDescription': 'Реестр и его параметры для этой клиники',
  'ai.views.files': 'Файлы',
  'ai.views.filesDescription': 'Модули и глоссарии для каждого прогона',
  'ai.views.credentials': 'Учётные данные',
  'ai.views.credentialsDescription': 'Ключи и данные доступа к сервисам',
  'ai.provider.title': 'Бэкенды обработки документов',
  'ai.provider.description': 'Каждая операция проводится там, куда её направляет эта клиника. Все три варианта сохраняются вместе.',
  'ai.provider.openai': 'OpenAI',
  'ai.provider.local': 'Локальный',
  'ai.provider.loading': 'Загрузка бэкендов обработки документов',
  'ai.provider.loadFailed': 'Не удалось загрузить настройки функций AI',
  'ai.provider.save': 'Сохранить настройки AI',
  'ai.provider.saving': 'Сохранение настроек AI',
  'ai.provider.saved': 'Настройки функций AI сохранены',
  'ai.provider.saveFailed': 'Не удалось сохранить настройки функций AI',
  'ai.provider.saveForbidden': 'Вам не разрешено менять настройки функций AI этой клиники',
  'ai.provider.saveConflict': 'Кто-то другой изменил эти настройки AI. Перезагрузите страницу и выберите снова.',
  'ai.provider.unsavedIntent': 'Выбор AI-бэкендов \u2014 ещё не сохранён',
  'ai.backend.deidentification': 'Анонимизация',
  'ai.backend.deidentificationNote': 'Удаляет идентификационные данные пациента из документа перед его отправкой из клиники.',
  'ai.backend.classification': 'Классификация',
  'ai.backend.classificationNote': 'Определяет, что представляет собой загруженный документ, и размещает его в нужном месте.',
  'ai.backend.laboratory': 'Извлечение результатов лабораторных исследований',
  'ai.backend.laboratoryNote': 'Считывает значения аналитов из лабораторного отчёта в карту пациента.',
  'ai.tester.title': 'Тестер документов',
  'ai.tester.open': 'Открыть тестер документов',
  'ai.tester.hint': 'Рабочее пространство Gradio только для разработчиков: классификация, извлечение лабораторных данных и анонимизация.',
  'ai.prompts.title': 'Реестр промптов',
  'ai.prompts.note': 'Промпты регистрируются сервисом ИИ. Записанные здесь параметры применяются только к этой клинике.',
  'ai.loading': 'Загрузка промптов ИИ',
  'ai.loadFailed': 'Не удалось загрузить промпты ИИ',
  'ai.forbidden': 'Вам не разрешено читать промпты ИИ этой клиники',
  'ai.empty': 'Промпты ИИ не зарегистрированы',
  'ai.prompt': 'Промпт',
  'ai.mode': 'Режим',
  'ai.modeResponses': 'Responses API',
  'ai.modeLocalVlm': "Локальная визуальная модель",
  'ai.modeAgent': 'Агент',
  'ai.model': 'Модель',
  'ai.turns': 'Ходы',
  'ai.output': 'Выходные токены',
  'ai.tools': 'Инструменты',
  'ai.providerState': 'Состояние провайдера',
  'ai.providerStateOn': 'Вкл.',
  'ai.providerStateOff': 'Выкл.',
  'ai.langfuse': 'Langfuse',
  'ai.reasoning': 'Рассуждения',
  'ai.reasoningUnset': 'Не задано',
  'ai.verbosity': 'Подробность',
  'ai.verbosityUnset': 'Не задано',
  'ai.serviceTier': 'Уровень обслуживания',
  'ai.timeout': 'Тайм-аут, секунд',
  'ai.webSearch': 'Веб-поиск',
  'ai.webSearchContext': 'Контекст веб-поиска',
  'ai.source': 'Параметры',
  'ai.sourceCustom': 'Изменены',
  'ai.sourceDefault': 'По умолчанию',
  'ai.tableLabel': 'Промпты ИИ',
  'ai.actions': 'Действия',
  'ai.editNamed': 'Изменить {name}',
  'ai.emptyDetail': 'Промпты регистрируются сервисом ИИ, а не с этого экрана.',
  'ai.noMatches': 'Нет промптов ИИ, соответствующих фильтрам',
  'ai.noMatchesDetail': 'Сбросьте фильтры, чтобы увидеть все зарегистрированные промпты.',
  'ai.editorTitle': 'Параметры выполнения',
  'ai.editorNote': 'Текст промпта и схема результата заданы сервисом ИИ. Эти параметры применяются только к этой клинике.',
  'ai.range': 'Допустимо:',
  'ai.outputContract': 'Схема результатов',
  'ai.outputContractFixed': 'Задана промптом',
  'ai.save': 'Сохранить',
  'ai.saving': 'Сохранение',
  'ai.saved': 'Сохранено',
  'ai.saveFailed': 'Не удалось сохранить параметры выполнения',
  'ai.saveForbidden': 'Вам не разрешено менять параметры промптов ИИ этой клиники',
  'ai.saveConflict': 'Кто-то другой изменил эти параметры. Закройте панель и откройте её снова.',
  'ai.invalidDraft': 'Значение вне допустимого диапазона',
  'ai.unsavedIntent': 'Настройки AI-промпта \u2014 правки, которые не были сохранены',
  'ai.reset': 'Восстановить значения по умолчанию',
  'ai.resetting': 'Восстановление',
  'ai.files.title': 'Файлы поддержки промптов',
  'ai.files.note': 'Прогоны перевода и диктовки прикрепляют к промпту эти файлы: модули исходного языка, модули целевого стиля и глоссарии. Каждый файл загружается в OpenAI один раз на учётную запись и далее используется по идентификатору.',
  'ai.files.loading': 'Загрузка файлов промптов ИИ',
  'ai.files.loadFailed': 'Не удалось загрузить файлы промптов ИИ',
  'ai.files.tableLabel': 'Файлы промптов ИИ',
  'ai.files.empty': 'Файлы поддержки не зарегистрированы',
  'ai.files.emptyDetail': 'Файлы поддержки приходят вместе с начальным набором данных.',
  'ai.files.file': 'Файл',
  'ai.files.slot': 'Роль',
  'ai.files.slotSource': 'Исходный язык',
  'ai.files.slotTarget': 'Целевой стиль',
  'ai.files.slotGlossary': 'Глоссарий',
  'ai.files.language': 'Язык',
  'ai.files.size': 'Размер',
  'ai.files.source': 'Содержимое',
  'ai.files.sourceSeed': 'Исходный',
  'ai.files.sourceCustom': 'Изменённый',
  'ai.files.openai': 'OpenAI',
  'ai.files.openaiCurrent': 'Загружено',
  'ai.files.openaiStale': 'Ожидает повторной загрузки',
  'ai.files.downloadNamed': 'Скачать {name}',
  'ai.files.replaceNamed': 'Заменить {name}',
  'ai.files.replacePicker': 'Выберите файл для замены',
  'ai.files.replaced': 'Файл {file} сохранён. Следующий прогон промпта загрузит его в OpenAI.',
  'ai.files.replaceInvalid': 'Заменяющий файл должен быть текстовым в UTF-8 размером до 1 МиБ.',
  'ai.files.replaceConflict': 'Кто-то другой изменил этот файл. В списке показано текущее содержимое; выберите файл заново.',
  'ai.files.replaceFailed': 'Не удалось заменить {file}',
}
